import type { LiveBreakHost, LiveStreamSegment } from "@hobbyshopify/backend-client";
import { type ReactNode, useState } from "react";
import { useObserver, useSubtree } from "subtree.js/react";
import { ColumnHead } from "../../../../components/live/ColumnHead";
import { CheckIcon, DownIcon, MoreIcon, PlayIcon, UpIcon } from "../../../../components/live/cockpit-icons";
import { formatBreakTime } from "../../../../core/primitives/lineup";
import { formatCents } from "../../../../core/primitives/live";
import { LineupActions, LineupState } from "./lineup-controller";

const ripping = (box: LiveBreakHost | null) => box?.status === "RANDOMIZED" || box?.status === "BREAKING";

/**
 * What the stream runs, in order, one line each. Tapping a box shows its spots
 * below; a planned row can go on screen, move, or come out — a box that comes
 * out goes back to the shop's queue still selling.
 */
export function Lineup({
  segments,
  breaks,
  over,
  shownBreakId,
  onPick,
}: {
  segments: readonly LiveStreamSegment[];
  breaks: readonly LiveBreakHost[];
  over: boolean;
  shownBreakId: string | null;
  onPick: (breakId: string) => void;
}) {
  const actions = useSubtree(LineupActions);
  const planned = segments.filter((s) => s.status === "PLANNED");
  const done = segments.filter((s) => s.status === "DONE").length;

  return (
    <section>
      <ColumnHead
        title="Line-up"
        aside={
          over ? (
            `${done} done`
          ) : (
            <button
              type="button"
              className="h-[30px] rounded-lg border border-[color:var(--color-accent)] px-2.5 text-[13px] font-bold text-[color:var(--color-accent)] hover:bg-[color:var(--color-accent)]/10"
              onClick={actions.addMore}
            >
              + Add
            </button>
          )
        }
      />
      <div className="flex flex-col gap-0.5 px-2 pt-2 pb-1">
        {segments.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-2 py-8 text-center">
            <p className="text-sm text-white/50">Nothing in the line-up yet.</p>
            {!over && (
              <button type="button" className="btn-primary h-11 px-6 font-bold" onClick={actions.addMore}>
                + Add a box
              </button>
            )}
          </div>
        ) : (
          segments.map((segment) => (
            <LineupRow
              key={segment.id}
              segment={segment}
              box={breaks.find((b) => b.id === segment.breakId) ?? null}
              first={planned[0]?.id === segment.id}
              last={planned.at(-1)?.id === segment.id}
              over={over}
              shown={segment.breakId !== null && segment.breakId === shownBreakId}
              onPick={onPick}
            />
          ))
        )}
      </div>
    </section>
  );
}

function LineupRow({
  segment,
  box,
  first,
  last,
  over,
  shown,
  onPick,
}: {
  segment: LiveStreamSegment;
  box: LiveBreakHost | null;
  first: boolean;
  last: boolean;
  over: boolean;
  shown: boolean;
  onPick: (breakId: string) => void;
}) {
  const state = useSubtree(LineupState);
  const actions = useSubtree(LineupActions);
  const observer = useObserver();
  const busyMove = observer.watch(state.busy);
  const failed = observer.watch(state.failure);
  const [menu, setMenu] = useState(false);

  const busy = busyMove?.id === segment.id;
  const failure = failed?.id === segment.id ? failed.message : null;
  const planned = segment.status === "PLANNED" && !over;
  const onScreen = segment.status === "CURRENT";
  const done = segment.status === "DONE";
  const red = onScreen && ripping(box);
  const cancellable =
    !over && box !== null && box.soldCount === 0 && (box.status === "ON_SALE" || box.status === "LOCKED");

  const tone = red
    ? "bg-[color:var(--color-danger)]/15"
    : onScreen
      ? "bg-[color:var(--color-accent)]/15"
      : shown
        ? "bg-white/[0.06]"
        : "hover:bg-white/[0.04]";
  const figures = red
    ? "text-[color:var(--color-danger)]"
    : onScreen
      ? "text-[color:var(--color-accent)]"
      : "text-white/50";

  return (
    <div>
      <div className={`flex h-10 items-center gap-2.5 rounded-lg px-2 ${tone} ${done ? "text-white/40" : ""}`}>
        <button
          type="button"
          className="flex h-full min-w-0 flex-1 items-center gap-2.5 text-left disabled:cursor-default"
          disabled={!box}
          onClick={() => box && onPick(box.id)}
        >
          <span
            className={`flex size-4 shrink-0 items-center justify-center ${
              red ? "text-[color:var(--color-danger)]" : onScreen ? "text-[color:var(--color-accent)]" : "text-white/40"
            }`}
          >
            {done ? (
              <CheckIcon />
            ) : onScreen ? (
              <PlayIcon />
            ) : (
              <span className="size-1.5 rounded-full border-[1.5px] border-current" />
            )}
          </span>
          <span className={`min-w-0 flex-1 truncate text-sm ${onScreen ? "font-extrabold" : "font-bold"}`}>
            {segment.title}
          </span>
          {box && (
            <span className={`shrink-0 text-xs font-semibold tabular-nums ${done ? "" : figures}`}>
              {box.soldCount}/{box.slotCount} · {formatCents(box.grossSoldCents)}
              {planned && box.breakNotBefore ? ` · ${formatBreakTime(box.breakNotBefore)}` : ""}
            </span>
          )}
        </button>

        {planned && (
          <span className="relative ml-1 flex shrink-0 gap-0.5">
            <RowButton label="Move up" disabled={busy || first} onClick={() => void actions.move(segment.id, -1)}>
              <UpIcon />
            </RowButton>
            <RowButton label="Move down" disabled={busy || last} onClick={() => void actions.move(segment.id, 1)}>
              <DownIcon />
            </RowButton>
            <RowButton
              label="Put on screen"
              accent
              disabled={busy}
              onClick={() => void actions.putOnScreen(segment.id)}
            >
              <PlayIcon size={14} />
            </RowButton>
            <RowButton label="Row menu" disabled={busy} onClick={() => setMenu(!menu)}>
              <MoreIcon size={14} />
            </RowButton>
            {menu && (
              <>
                <button
                  type="button"
                  aria-label="Close menu"
                  className="fixed inset-0 z-10 cursor-default"
                  onClick={() => setMenu(false)}
                />
                <div className="absolute top-8 right-0 z-20 w-52 rounded-xl border border-[color:var(--color-line)] bg-[color:var(--color-app-elevated)] p-1.5 shadow-xl">
                  <MenuItem
                    onClick={() => {
                      setMenu(false);
                      void actions.remove(segment.id);
                    }}
                  >
                    {box ? "Back to the queue" : "Remove"}
                  </MenuItem>
                  {cancellable && box && (
                    <MenuItem
                      danger
                      onClick={() => {
                        setMenu(false);
                        void actions.cancelBreak(segment.id, box.id);
                      }}
                    >
                      Cancel this break
                    </MenuItem>
                  )}
                </div>
              </>
            )}
          </span>
        )}
      </div>
      {failure && <p className="note note-danger mx-2 mt-1">{failure}</p>}
    </div>
  );
}

function RowButton({
  label,
  accent = false,
  disabled,
  onClick,
  children,
}: {
  label: string;
  accent?: boolean;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={`flex size-7 items-center justify-center rounded-md hover:bg-white/10 disabled:opacity-25 ${
        accent ? "text-[color:var(--color-accent)]" : "text-white/75"
      }`}
    >
      {children}
    </button>
  );
}

function MenuItem({
  danger = false,
  onClick,
  children,
}: {
  danger?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex h-10 w-full items-center rounded-lg px-3 text-left text-[13px] font-semibold hover:bg-white/10 ${
        danger ? "text-[color:var(--color-danger)]" : ""
      }`}
    >
      {children}
    </button>
  );
}
